import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  validateInvitation,
  registerWithInvitation,
} from '../services/invitationService';
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [invitationEmail, setInvitationEmail] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });

  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setValidationError('Invalid invitation link. Please check your email for the correct link.');
      setValidating(false);
      return;
    }

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    try {
      setValidating(true);
      setValidationError(null);
      const response = await validateInvitation(token);
      setInvitationEmail(response.data.invitation.email);
    } catch (err: any) {
      setValidationError(
        err.response?.data?.error?.message ||
          'This invitation is invalid or has expired'
      );
    } finally {
      setValidating(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError(null);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setRegisterError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      setRegisterError('Password must be at least 8 characters long');
      return;
    }

    try {
      setRegistering(true);
      await registerWithInvitation({
        token,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });

      // Registration successful - redirect to login
      alert('Account created successfully! Please log in.');
      navigate('/login');
    } catch (err: any) {
      setRegisterError(
        err.response?.data?.error?.message || 'Failed to create account'
      );
    } finally {
      setRegistering(false);
    }
  };

  if (validating) {
    return (
      <div className="register-container">
        <div className="register-card">
          <div className="loading">Validating invitation...</div>
        </div>
      </div>
    );
  }

  if (validationError) {
    return (
      <div className="register-container">
        <div className="register-card">
          <div className="validation-error">
            <h2>Invalid Invitation</h2>
            <p>{validationError}</p>
            <button onClick={() => navigate('/login')} className="btn-primary">
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <h1>Join Zippel Staff Portal</h1>
          <p>Complete your registration to get started</p>
          <div className="invitation-email">
            Invitation sent to: <strong>{invitationEmail}</strong>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                disabled={registering}
                placeholder="John"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                disabled={registering}
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              dir="ltr"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={registering}
              placeholder="At least 8 characters"
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              dir="ltr"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={registering}
              placeholder="Re-enter your password"
            />
          </div>

          {registerError && (
            <div className="error-message">{registerError}</div>
          )}

          <button type="submit" className="btn-submit" disabled={registering}>
            {registering ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="register-footer">
          Already have an account?{' '}
          <button onClick={() => navigate('/login')} className="link-button">
            Log in
          </button>
        </div>
      </div>
    </div>
  );
};

export default Register;
